// QR Kiosk v2 — shared route-scoped machine resolver (guest-kiosk resilience fix).
//
// Every machine-reading kiosk page (dashboard, reports, tasks, spec-sheet, manuals,
// company, create-report) gated BOTH its render AND its downstream data fetches on
//   machine.internalNumber?.toLowerCase() === routeKey.toLowerCase()
// to ignore the single shared publicSlice.machine field until it belongs to THIS route
// (KV2-1-e: no stale plate-A flash while plate-B refetches).
//
// BUG (guest tier): GET /v1/public/machines/{number} returns HTTP 200 for a guest, but
// with the SEC-REQ-2 curated PublicMachineViewModel — because a guest is an authenticated
// non-owner (GetUserCustomerIdAsync throws for a guest → the ownership check falls to the
// curated branch). That curated shape OMITS internalNumber. The internalNumber===routeKey
// guard therefore NEVER matched, so `m` stayed undefined and every page hung on its
// skeleton forever — and because the call FULFILLED (200), the .failed error branch never
// fired either (no ErrorCallout, no AccessDenied). Company logins were fine because the
// owner receives the full MachineViewModel (internalNumber present).
//
// FIX (v2-only, additive; publicSlice untouched; auth NOT weakened — the server-side
// GuestMachineScopeFilter still enforces single-machine scope on every request):
//   1. Match on the request-keyed load-state (loadedForKey — the last FULFILLED getMachine
//      was for THIS routeKey) in addition to the internalNumber. loadedForKey is keyed by
//      the request arg, not the payload shape, so it resolves the curated payload while
//      keeping the anti-stale-flash guarantee (during an A→B scan it stays false until B
//      resolves).
//   2. Backfill internalNumber from routeKey when the curated payload omitted it. The
//      :id / ?machine route value IS this machine's internalNumber (the QR/business number;
//      for a guest it equals the token's machine_scope), so this is a known-correct identity
//      — and it re-enables the internalNumber-keyed fetches (getServiceReports / getDocuments)
//      and every tile/CTA navigation the pages build from machine.internalNumber.
import { useAppSelector } from "../../../shared/hooks/sharedHooks";
import { Machine } from "../../models/machinesModels";
import { useSurfaceState } from "./useSurfaceState";

export function useKioskRouteMachine(routeKey?: string): Machine | undefined {
  const machine = useAppSelector((s) => s.public.machine);
  const st = useSurfaceState("machine", routeKey);

  if (!machine || !routeKey) return undefined;

  const matched =
    machine.internalNumber?.toLowerCase() === routeKey.toLowerCase() || st.loadedForKey;
  if (!matched) return undefined;

  // Curated guest/non-owner payload omits internalNumber → backfill from the route value
  // (a known-correct identity; see the header note). Owner payload already has it → no-op.
  return machine.internalNumber ? machine : { ...machine, internalNumber: routeKey };
}

export default useKioskRouteMachine;
